import {
  QUERY_ADD_URBAN_FEATURE,
  QUERY_REMOVE_URBAN_FEATURE,
  QUERY_UPDATE_URBAN_FEATURE,
  QUERY_URBAN_FEATURE,
} from "@/services/web/api-urban-feature.const";
import { hideGlobalLoading, showGlobalLoading } from "@/hooks/use-global-loading";
import { useContext, useEffect, useMemo } from "react";
import { useTrackedMutation, useTrackedQuery } from "@/hooks/apollo-insights-hooks";

import { CityContext } from "@/contexts/city-context-provider";
import { UrbanFeatureType } from "@/types/gql/graphql";
import { skipToken } from "@apollo/client/react";
import { useDebounce } from "@/hooks/lib-ui";

type QueryUrbanFeatureParams = {
  bounding?: number[][] | undefined;
  types?: UrbanFeatureType[];
};

export const useQueryUrbanFeature = ({ bounding, types }: QueryUrbanFeatureParams) => {
  const { currentCity } = useContext(CityContext);
  const nearBy = useDebounce(bounding, 500);

  const refetchQueriesConfig = useMemo(() => {
    if (!currentCity?.id) {
      return [];
    }
    return [
      {
        query: QUERY_URBAN_FEATURE,
        variables: {
          cityId: currentCity.id,
          boundingbox: nearBy,
          types,
        },
      },
    ];
  }, [currentCity?.id, nearBy, types]);

  const { data, loading } = useTrackedQuery(
    QUERY_URBAN_FEATURE,
    currentCity?.id
      ? {
          variables: {
            cityId: currentCity.id,
            boundingbox: nearBy,
            types,
          },
          actionName: "QueryUrbanFeaturesByBounding",
        }
      : skipToken,
  );

  useEffect(() => {
    if (loading) {
      showGlobalLoading();
    } else {
      hideGlobalLoading();
    }
  }, [loading]);

  const [createUrbanFeature] = useTrackedMutation(QUERY_ADD_URBAN_FEATURE, {
    actionName: "AddUrbanFeatureToCity",
    refetchQueries: refetchQueriesConfig,
    awaitRefetchQueries: true,
  });

  const [updateUrbanFeature] = useTrackedMutation(QUERY_UPDATE_URBAN_FEATURE, {
    actionName: "UpdateUrbanFeature",
    refetchQueries: refetchQueriesConfig,
    awaitRefetchQueries: true,
  });

  const [removeUrbanFeature] = useTrackedMutation(QUERY_REMOVE_URBAN_FEATURE, {
    actionName: "RemoveUrbanFeature",
    refetchQueries: refetchQueriesConfig,
    awaitRefetchQueries: true,
  });

  const urbanFeatureList = useMemo(() => {
    return data?.urbanFeaturesByBounding || [];
  }, [data]);

  return {
    nearBy,
    urbanFeatureList,
    loading,
    createUrbanFeature,
    updateUrbanFeature,
    removeUrbanFeature,
  };
};
